import { callFinnhub } from "./finnhubClient";
// Real economic calendar (David, 2026-09-26). Only three kinds of event, each
// from a live source with its name on it:
//   • the next quarterly report of each HOLDING   · Finnhub (per symbol)
//   • four scheduled US macro releases            · FRED release calendar
//   • NYSE holidays and half days                 · Finnhub market-holiday
//   • FOMC scheduled meetings                     · Federal Reserve calendar,
//     a fixed list checked against the page (constants/fomc.js, sprint 13)
// Not shown: the whole-market earnings list, IPOs and news headlines (a
// headline is not a scheduled event), dividends (no source yet). FRED's FOMC
// release is marked on every day, so the meetings come from the Fed's page.
import { fetchNextReport } from "./nextReport";
import { FOMC_MEETINGS } from "../constants/fomc";


const _cache = {};
const TTL = 10 * 60 * 1000; // 10 min
const FETCH_TIMEOUT = 12000; // abort a stuck endpoint so it can't block the list

// `load` goes through the Netlify proxy; the key is no longer in the browser.
// The timeout stays and still matters — it now guards a hung FUNCTION rather
// than a hung Finnhub, and without it one slow call would keep the whole
// Promise.all (and therefore the event list) pending for minutes.
async function getJSON(cacheKey, load) {
  const hit = _cache[cacheKey];
  if (hit && Date.now() - hit.ts < TTL) return hit.data;
  let timer;
  try {
    const data = await Promise.race([
      load(),
      new Promise((_, rej) => { timer = setTimeout(() => rej(new Error("timeout")), FETCH_TIMEOUT); }),
    ]);
    if (data != null) _cache[cacheKey] = { data, ts: Date.now() };
    return data;
  } catch (e) {
    console.warn(`[calendar] ${cacheKey}:`, e.message);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function iso(d) { return d.toISOString().slice(0, 10); }

// Static Hebrew names for US market holidays (factual, no API). Falls back to
// the original English eventName when a holiday isn't in the map.
const US_HOLIDAY_HE = {
  "New Year's Day": "ראש השנה האזרחית",
  "Martin Luther King, Jr. Day": "יום מרטין לותר קינג",
  "Washington's Birthday": "יום וושינגטון",
  "Presidents Day": "יום הנשיאים",
  "Good Friday": "יום שישי הטוב",
  "Memorial Day": "יום הזיכרון (ארה\"ב)",
  "Juneteenth National Independence Day": "יום ג'ונטינת'",
  "Juneteenth": "יום ג'ונטינת'",
  "Independence Day": "יום העצמאות (ארה\"ב)",
  "Labor Day": "יום העבודה (ארה\"ב)",
  "Thanksgiving Day": "חג ההודיה",
  "Christmas Day": "חג המולד",
};

// ── US market holidays (real, Finnhub) ───────────────────────────────────────
async function fetchHolidays(from, to) {
  const data = await getJSON("hol_US", () => callFinnhub("holidays").then(d => d?.holidays ?? null));
  const rows = data?.data;
  if (!Array.isArray(rows)) return [];
  return rows
    .filter(r => r.atDate && r.atDate >= from && r.atDate <= to)
    .map(r => ({
      id: `hol-${r.atDate}`,
      date: r.atDate,
      time: r.tradingHour ? "מסחר חלקי" : "ללא מסחר",
      type: "holiday",
      // A half day is a trading day: its title says so, not "ללא מסחר".
      title: `${r.tradingHour ? 'מסחר מקוצר (ארה"ב)' : 'ללא מסחר (ארה"ב)'} · ${US_HOLIDAY_HE[r.eventName] || r.eventName}`,
      source: "Finnhub · NYSE/NASDAQ",
      symbols: [],
      mine: false,
      detail: { tradingHour: r.tradingHour || "" },
    }));
}

// ── Scheduled US macro releases (real, FRED release calendar) ───────────────
const RELEASE_HE = {
  cpi:  "מדד המחירים לצרכן (CPI)",
  jobs: "דוח התעסוקה",
  gdp:  "תוצר (GDP)",
  pce:  "הכנסה והוצאה אישית (PCE)",
};
async function fetchMacroReleases(from, to) {
  const data = await getJSON("fred_releases", () =>
    fetch("/.netlify/functions/marketdata?op=releases").then(r => (r.ok ? r.json() : null)));
  const rows = data?.releases;
  if (!Array.isArray(rows)) return [];
  return rows
    .filter(r => RELEASE_HE[r.key] && r.date >= from && r.date <= to)
    .map(r => ({
      id: `macro-${r.key}-${r.date}`,
      date: r.date,
      time: "",
      type: "macro",
      title: RELEASE_HE[r.key],
      source: "FRED · לוח הפרסומים",
      symbols: [],
      mine: false,
      detail: { releaseId: r.releaseId, key: r.key },
    }));
}

// ── FOMC scheduled meetings (fixed list, Federal Reserve) ──────────────────
// Filed under "macro" so the calendar's filter covers them; `kindLabel` says
// what they are, since a meeting is not a data release.
export function fomcEvents(from, to) {
  return FOMC_MEETINGS
    .filter(m => m.date >= from && m.date <= to)
    .map(m => ({
      id: `fomc-${m.date}`,
      date: m.date,
      time: "",
      type: "macro",
      kindLabel: "ישיבת FOMC",
      title: "החלטת הריבית של הפד",
      source: "Federal Reserve · לוח הישיבות",
      symbols: [],
      mine: false,
      detail: { key: "fomc", start: m.start, sep: !!m.sep },
    }));
}

// The next report of each of the user's symbols, from the per-symbol pull
// (cached a day). Same event shape as fetchEarnings.
async function fetchOwnReports(from, to, symbols) {
  const list = [...new Set(symbols)].slice(0, 25);
  // Same guard as getJSON: one hung call must not hold the whole list on its
  // loading placeholders.
  const guarded = s => Promise.race([
    fetchNextReport(s).catch(() => null),
    new Promise(r => setTimeout(() => r(null), FETCH_TIMEOUT)),
  ]);
  const reps = await Promise.all(list.map(guarded));
  return list.flatMap((sym, i) => {
    const r = reps[i];
    if (!r || r.none || !r.date || r.date < from || r.date > to) return [];
    return [{
      id: `earn-${sym}-${r.date}`,
      date: r.date,
      time: r.hour === "bmo" ? "לפני פתיחה" : r.hour === "amc" ? "אחרי סגירה" : r.hour === "dmh" ? "במהלך המסחר" : "",
      slot: ["bmo", "dmh", "amc"].includes(r.hour) ? r.hour : null,
      type: "earnings",
      title: `דוח רבעוני · ${sym}`,
      source: "Finnhub",
      symbols: [sym],
      mine: true,
      detail: { epsEstimate: r.epsEstimate, revenueEstimate: r.revenueEstimate },
    }];
  });
}

// Aggregate all real sources for a date window. Any source that fails is
// simply absent · never replaced with placeholder data.
export async function fetchCalendarEvents({ from, to, userSymbols = [] }) {
  const [own, macro, hols] = await Promise.all([
    fetchOwnReports(from, to, userSymbols),
    fetchMacroReleases(from, to),
    fetchHolidays(from, to),
  ]);
  return [...own, ...macro, ...fomcEvents(from, to), ...hols];
}

// Convenience: default 30-day forward window from today
export function defaultRange() {
  const today = new Date();
  const to = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  return { from: iso(today), to: iso(to) };
}

// The market's week for the home screen (sprint 13, 2.1): the scheduled macro
// releases and the market holidays, from the same sources as the calendar.
export async function fetchMarketWeek({ from, to }) {
  const [macro, hols] = await Promise.all([fetchMacroReleases(from, to), fetchHolidays(from, to)]);
  return [...macro, ...fomcEvents(from, to), ...hols];
}
